import React from 'react';
import { Dialog, DialogContent, TextField, List, ListItemButton, ListItemText, InputAdornment, Typography } from '@mui/material';
import { Search } from '@mui/icons-material';
import { getApiUrl } from '../utils/apiConfig';

export default function CommandPalette({ open, onClose, onRun }) {
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState([]);

  const extractList = React.useCallback((payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.patients)) return payload.patients;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
  }, []);

  React.useEffect(() => {
    if (!open) return;
    setQuery('');
    setResults([]);
  }, [open]);

  // Placeholder: a unified local filter; wire to API later
  React.useEffect(() => {
    let abort = false;
    async function run() {
      if (!query || query.length < 2) {
        setResults([]);
        return;
      }
      const token = localStorage.getItem('_at') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const [pRes, tRes, rRes] = await Promise.all([
          fetch(getApiUrl('/api/patients') + `?q=${encodeURIComponent(query)}&limit=5`, { headers }),
          fetch(getApiUrl('/api/order-templates') + `?limit=5`, { headers }),
          fetch(getApiUrl('/api/protocol-config'), { headers }),
        ]);
        const [pJson, tJson, rJson] = await Promise.all([
          pRes.ok ? pRes.json() : { data: [] },
          tRes.ok ? tRes.json() : { data: [] },
          rRes.ok ? rRes.json() : { data: [] },
        ]);
        if (abort) return;
        const items = [];
        extractList(pJson).forEach(p =>
          items.push({
            label: `Patient: ${p.lastName}, ${p.firstName} (${p.mrn || p.id})`,
            caption: 'Patients',
            action: { type: 'nav', to: `/patient/${p.id}` },
          })
        );
        extractList(tJson).forEach(t =>
          items.push({
            label: `Order Template: ${t.displayName || t.name}`,
            caption: 'Orders',
            action: { type: 'nav', to: `/orders?template=${encodeURIComponent(t.name)}` },
          })
        );
        extractList(rJson).filter((r)=>{
          try { return JSON.stringify(r).toLowerCase().includes(query.toLowerCase()); } catch { return false; }
        }).slice(0,5).forEach(r =>
          items.push({
            label: `Rule: ${r.name}`,
            caption: 'Protocols',
            action: { type: 'nav', to: `/protocols?rule=${encodeURIComponent(r.name)}` },
          })
        );
        // Fallback suggestions
        if (items.length === 0) {
          items.push({ label: `Search patients for “${query}”`, action: { type: 'nav', to: `/patients?q=${encodeURIComponent(query)}` } });
          items.push({ label: `Search protocols for “${query}”`, action: { type: 'nav', to: `/protocols?q=${encodeURIComponent(query)}` } });
        }
        setResults(items);
      } catch {
        setResults([
          { label: `Search patients for “${query}”`, action: { type: 'nav', to: `/patients?q=${encodeURIComponent(query)}` } },
        ]);
      }
    }
    run();
    return () => {
      abort = true;
    };
  }, [query]);

  const handleRun = item => {
    onRun?.(item.action);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          autoFocus
          fullWidth
          placeholder="Type a command or search…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <List dense sx={{ mt: 1, maxHeight: 320, overflowY: 'auto' }}>
          {results.map((r, i) => (
            <ListItemButton key={i} onClick={() => handleRun(r)}>
              <ListItemText primary={r.label} secondary={r.caption && (<Typography variant="caption">{r.caption}</Typography>)} />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
}
